<template>
  <div class="itinerario-elegante">
    <div class="encabezado">
      <h2 class="titulo">Itinerario</h2>
      <p class="subtitulo">Itinerario de Nuestra Boda</p>
    </div>

    <div class="linea-tiempo-horizontal" ref="timelineRef">
      <div class="linea-progreso" :style="{ height: progreso + '%' }"></div>

      <div class="eventos-container">
        <div
          v-for="(evento, index) in eventos"
          :key="index"
          :ref="el => { if (el) eventoRef[index] = el }"
          class="evento"
          :class="{
            'evento-activo': index <= eventoActivo,
            'evento-animado': eventosAnimados[index]
          }"
        >
          <div class="marcador" :ref="el => { if (el) marcadorRef[index] = el }">
            <div class="circulo-exterior">
              <div class="circulo-interior"></div>
              <i :class="evento.icono" class="icono-evento"></i>
            </div>
            <div class="fecha">{{ evento.hora }}</div>
          </div>

          <div class="tarjeta-evento" :ref="el => { if (el) tarjetaRef[index] = el }">
            <h3 class="titulo-evento" :ref="el => { if (el) tituloRef[index] = el }">{{ evento.titulo }}</h3>
            <p class="descripcion" v-if="evento.lugar" :ref="el => { if (el) descripcionRef[index] = el }">{{ evento.lugar }}</p>
            <div class="decoracion-corazon" :ref="el => { if (el) corazonRef[index] = el }">
              <i class="fas fa-heart"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { gsap } from 'gsap'

export default {
  name: 'ItinerarioElegante',
  setup() {
    const timelineRef = ref(null)
    const eventoRef = ref([])
    const marcadorRef = ref([])
    const tarjetaRef = ref([])
    const tituloRef = ref([])
    const descripcionRef = ref([])
    const corazonRef = ref([])

    const progreso = ref(0)
    const eventoActivo = ref(-1)
    const eventosAnimados = ref([])
    const observer = ref(null)

    const eventos = [
      { titulo: 'Ceremonia Civil', hora: '18:00 ', lugar: 'Hotel Gran Palacio', icono: 'fas fa-landmark' },
      { titulo: 'Recepción', hora: '20:00  ', lugar: 'Casa Paraiso', icono: 'fas fa-glass-cheers' },
      { titulo: 'Entrada novios', hora: '20:30 ', icono: 'fas fa-heart' },
      { titulo: 'Cena', hora: '21:30 ', icono: 'fas fa-utensils' },
      { titulo: 'Fiesta', hora: '22:00  ', icono: 'fas fa-music' }
    ]

    const initObserver = () => {
      observer.value = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const index = eventoRef.value.findIndex(el => el === entry.target)
          if (entry.isIntersecting && index !== -1 && !eventosAnimados.value[index]) {
            eventosAnimados.value[index] = true

            const delayBase = index * 0.3

            if (marcadorRef.value[index]) {
              gsap.to(marcadorRef.value[index], {
                opacity: 1,
                x: 0,
                duration: 0.8,
                delay: delayBase,
                ease: 'power3.out'
              })
            }

            if (tarjetaRef.value[index]) {
              gsap.to(tarjetaRef.value[index], {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.9,
                delay: delayBase + 0.2,
                ease: 'power3.out'
              })
            }

            if (tituloRef.value[index]) {
              gsap.to(tituloRef.value[index], {
                opacity: 1,
                y: 0,
                duration: 0.6,
                delay: delayBase + 0.4,
                ease: 'back.out(1.7)'
              })
            }

            if (descripcionRef.value[index]) {
              gsap.to(descripcionRef.value[index], {
                opacity: 1,
                x: 0,
                duration: 0.6,
                delay: delayBase + 0.6,
                ease: 'power2.out'
              })
            }

            if (corazonRef.value[index]) {
              gsap.to(corazonRef.value[index], {
                opacity: 1,
                scale: 1,
                rotate: 0,
                duration: 0.7,
                delay: delayBase + 0.8,
                ease: 'elastic.out(1, 0.5)'
              })
            }

            eventoActivo.value = Math.max(eventoActivo.value, index)
            const total = eventos.length
            const visibles = eventosAnimados.value.filter(Boolean).length
            progreso.value = (visibles / total) * 100
          }
        })
      }, {
        threshold: 0.3,
        rootMargin: '0px 0px -20px 0px'
      })

      eventoRef.value.forEach(el => {
        if (el) observer.value.observe(el)
      })
    }

    onMounted(async () => {
      eventoRef.value = []
      marcadorRef.value = []
      tarjetaRef.value = []
      tituloRef.value = []
      descripcionRef.value = []
      corazonRef.value = []
      eventosAnimados.value = new Array(eventos.length).fill(false)

      await nextTick()
      await nextTick()

      // Ocultar todos los elementos antes de animar
      eventos.forEach((_, index) => {
        if (marcadorRef.value[index]) {
          gsap.set(marcadorRef.value[index], { opacity: 0, x: -50 })
        }
        if (tarjetaRef.value[index]) {
          gsap.set(tarjetaRef.value[index], { opacity: 0, y: 50, scale: 0.95 })
        }
        if (tituloRef.value[index]) {
          gsap.set(tituloRef.value[index], { opacity: 0, y: 30 })
        }
        if (descripcionRef.value[index]) {
          gsap.set(descripcionRef.value[index], { opacity: 0, x: -30 })
        }
        if (corazonRef.value[index]) {
          gsap.set(corazonRef.value[index], { opacity: 0, scale: 0.5, rotate: -45 })
        }
      })

      initObserver()
    })

    onUnmounted(() => {
      if (observer.value) observer.value.disconnect()
    })

    return {
      timelineRef,
      eventoRef,
      marcadorRef,
      tarjetaRef,
      tituloRef,
      descripcionRef,
      corazonRef,
      eventos,
      progreso,
      eventoActivo,
      eventosAnimados
    }
  }
}
</script>



<style scoped>
/* Estilos base mejorados */
.itinerario-elegante {
  padding: 10px 20px;
  font-family: 'Cormorant Garamond', serif;
  position: relative;
  overflow: hidden;
}

.encabezado {
  text-align: center;
  margin-bottom: 60px;
}

.titulo {
  font-size: 2.8rem;
  color: #3d2e28;
  margin-bottom: 10px;
  letter-spacing: 1px;
  position: relative;
  display: inline-block;
}

.titulo:after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 2px;
  background: linear-gradient(to right, transparent, #c19a8b, transparent);
}

.subtitulo {
  font-size: 1rem;
  color: #7a6a65;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-top: 15px;
  font-weight: 500;
}

/* Línea de tiempo */
.linea-tiempo-horizontal {
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 0 80px;
}

.linea-progreso {
  position: absolute;
  left: 80px; /* posición fija a la izquierda */
  top: 0;
  width: 3px; /* ancho fijo */
  background: linear-gradient(to bottom, #c19a8b, #a07f73);
  z-index: 1;
  transition: height 0.8s ease-out;
  border-radius: 3px;
}


/* Contenedor de eventos */
.eventos-container {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}
.evento {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem;
 
}



/* Marcadores de tiempo */
.marcador {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.circulo-exterior {
  width: 60px; /* Aumentado para mejor visibilidad */
  height: 60px; /* Aumentado para mejor visibilidad */
  border-radius: 50%;
  background-color: #fff;
  border: 2px solid #e8c4b8;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.circulo-interior {
  width: 40px; /* Aumentado para mejor visibilidad */
  height: 40px; /* Aumentado para mejor visibilidad */
  border-radius: 50%;
  background-color: #f5e6e1;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;
}

.icono-evento {
  position: absolute;
  font-size: 24px; /* Aumentado para mejor visibilidad */
  color: #8e6e65;
  z-index: 3;
}

.fecha {
  margin-top: 12px;
  font-size: 15px;
  color: #7a6a65;
  font-weight: 500;
}

/* Tarjetas de evento */
.tarjeta-evento {
  padding: 25px;
  border-radius: 8px;
  width: 100%;
  position: relative;
  transition: all 0.3s ease;
  text-align: center;
  min-height: 140px;
}

.titulo-evento {
  font-size: 1.3rem; /* Aumentado para mejor legibilidad */
  color: #3d2e28;
  margin-bottom: 10px;
  font-weight: 600;
  line-height: 1.3;
}

.descripcion {
  font-size: 1rem; /* Aumentado para mejor legibilidad */
  color: #6a5a54;
  margin: 0;
  font-style: italic;
  line-height: 1.4;
}

.decoracion-corazon {
  position: absolute;
  bottom: -12px;
  right: -12px;
  width: 36px;
  height: 36px;
  background-color: #f5e6e1;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.decoracion-corazon i {
  color: #c19a8b;
  font-size: 14px;
}

/* Estados interactivos */
.evento:hover .circulo-exterior {
  border-color: #c19a8b;
  transform: scale(1.1);
}

.evento:hover .circulo-interior {
  background-color: #f0d8d1;
}

.evento-activo .circulo-exterior {
  border-color: #a07f73;
}

.evento-activo .circulo-interior {
  background-color: #e8c4b8;
}

.evento-activo .icono-evento {
  color: #3c3836;
  font-size: 26px; /* Ligeramente más grande cuando está activo */
}

/* Responsive */
@media (max-width: 768px) {
  .linea-tiempo-horizontal {
    padding: 40px 0 60px;
  }
  
  .eventos-container {
    flex-direction: column;
    align-items: center;
  }
  
  .evento {
    width: 80%;
    margin-bottom: 40px;
    flex-direction: row;
    align-items: flex-start;
  }
  
  .marcador {
    margin-right: 20px;
    margin-bottom: 0;
  }
  
  .linea-progreso {
    height: 100%;
    width: 3px;
    top: 0;
    left: 30px; /* Ajustado para el círculo más grande */
  }
  
  .evento:last-child {
    margin-bottom: 0;
  }
}

@media (max-width: 480px) {
  .titulo {
    font-size: 2.2rem;
  }
  
  .evento {
    width: 90%;
  }
  
  .tarjeta-evento {
    padding: 20px;
  }
  
  .circulo-exterior {
    width: 50px;
    height: 50px;
  }
  
  .circulo-interior {
    width: 34px;
    height: 34px;
  }
  
  .icono-evento {
    font-size: 20px;
  }
}
</style>